import { Context, Data, Effect, Layer } from 'effect';

import {
  SqlExecutor,
  layerMemory as sqlExecutorLayerMemory,
  type SqlBinding,
  type SqlExecutorError,
  type SqlRow,
} from './sql-executor.js';

const TypeId = 'okrapdf/core/persistence/IndexStore/TypeId' as const;
const ErrorTypeId = 'okrapdf/core/persistence/IndexStore/IndexStoreError' as const;

export interface IndexCollectionRow {
  readonly id: string;
  readonly user_id: string;
  readonly name: string;
  readonly slug: string | null;
  readonly description: string | null;
  readonly visibility: string;
  readonly cloned_from: string | null;
  readonly allow_clone: number;
  readonly webhook_url: string | null;
  readonly extraction_schema: string | null;
  readonly created_at: string;
}

export interface IndexCollectionDocumentRow {
  readonly document_id: string;
  readonly added_at: string;
  readonly file_name: string | null;
  readonly phase: string | null;
  readonly pages_total: number | null;
  readonly total_nodes: number | null;
  readonly source: string | null;
}

export interface IndexStore {
  readonly [TypeId]: typeof TypeId;
  readonly queryRows: <Row extends SqlRow = SqlRow>(
    query: string,
    ...bindings: SqlBinding[]
  ) => Effect.Effect<ReadonlyArray<Row>, IndexStoreError>;
  readonly one: <Row extends SqlRow = SqlRow>(
    query: string,
    ...bindings: SqlBinding[]
  ) => Effect.Effect<Row | undefined, IndexStoreError>;
  readonly exec: (
    query: string,
    ...bindings: SqlBinding[]
  ) => Effect.Effect<void, IndexStoreError>;
  readonly getCollection: (
    id: string,
  ) => Effect.Effect<IndexCollectionRow | undefined, IndexStoreError>;
  readonly countCollectionDocuments: (
    collectionId: string,
  ) => Effect.Effect<number, IndexStoreError>;
  readonly listCollectionDocumentIds: (
    collectionId: string,
    options?: { readonly publicOnly?: boolean },
  ) => Effect.Effect<ReadonlyArray<string>, IndexStoreError>;
  readonly listCollectionDocuments: (
    collectionId: string,
    options?: { readonly publicOnly?: boolean },
  ) => Effect.Effect<ReadonlyArray<IndexCollectionDocumentRow>, IndexStoreError>;
}

export type IndexStoreMethod =
  | 'queryRows'
  | 'one'
  | 'exec'
  | 'getCollection'
  | 'countCollectionDocuments'
  | 'listCollectionDocumentIds'
  | 'listCollectionDocuments';

export class IndexStoreError extends Data.TaggedError('IndexStoreError')<{
  readonly method: IndexStoreMethod;
  readonly message: string;
  readonly cause?: unknown;
}> {
  readonly [ErrorTypeId]: typeof ErrorTypeId = ErrorTypeId;
}

export const IndexStore: Context.Service<IndexStore, IndexStore> = Context.Service(
  'okrapdf/core/persistence/IndexStore',
);

export interface IndexStoreMakeOptions {
  readonly sql: SqlExecutor;
}

function storeError(method: IndexStoreMethod, cause: SqlExecutorError): IndexStoreError {
  return new IndexStoreError({
    method,
    message: `Unable to execute IndexStore.${method}`,
    cause,
  });
}

function listCollectionDocumentIdsSql(publicOnly: boolean): string {
  if (publicOnly) {
    return `SELECT cd.document_id FROM collection_documents cd
       LEFT JOIN documents d ON d.doc_id = cd.document_id
       WHERE cd.collection_id = ?
         AND d.is_public = 1`;
  }
  return 'SELECT document_id FROM collection_documents WHERE collection_id = ?';
}

function listCollectionDocumentsSql(publicOnly: boolean): string {
  return `
    SELECT cd.document_id, cd.added_at,
           d.file_name, d.phase, d.pages_total, d.total_nodes, d.source
    FROM collection_documents cd
    LEFT JOIN documents d ON d.doc_id = cd.document_id
    WHERE cd.collection_id = ?
      ${publicOnly ? 'AND d.is_public = 1' : ''}
    ORDER BY cd.added_at
  `;
}

/** Construct an IndexStore from the single backing SqlExecutor port. */
export function make(options: IndexStoreMakeOptions): IndexStore {
  const execRows = <Row extends SqlRow = SqlRow>(
    method: IndexStoreMethod,
    query: string,
    ...bindings: SqlBinding[]
  ): Effect.Effect<ReadonlyArray<Row>, IndexStoreError> =>
    Effect.mapError(options.sql.exec<Row>(query, ...bindings), (cause) => storeError(method, cause));

  return IndexStore.of({
    [TypeId]: TypeId,
    queryRows: (query, ...bindings) => execRows('queryRows', query, ...bindings),
    one: <Row extends SqlRow = SqlRow>(
      query: string,
      ...bindings: SqlBinding[]
    ): Effect.Effect<Row | undefined, IndexStoreError> =>
      Effect.map(execRows<Row>('one', query, ...bindings), (rows): Row | undefined => rows[0]),
    exec: (query, ...bindings) => Effect.asVoid(execRows('exec', query, ...bindings)),
    getCollection: (id) =>
      Effect.map(
        execRows<IndexCollectionRow & SqlRow>(
          'getCollection',
          'SELECT * FROM collections WHERE id = ?',
          id,
        ),
        (rows) => rows[0],
      ),
    countCollectionDocuments: (collectionId) =>
      Effect.map(
        execRows<{ readonly count: number }>(
          'countCollectionDocuments',
          'SELECT COUNT(*) as count FROM collection_documents WHERE collection_id = ?',
          collectionId,
        ),
        (rows) => rows[0]?.count ?? 0,
      ),
    listCollectionDocumentIds: (collectionId, options) =>
      Effect.map(
        execRows<{ readonly document_id: string }>(
          'listCollectionDocumentIds',
          listCollectionDocumentIdsSql(options?.publicOnly === true),
          collectionId,
        ),
        (rows) => rows.map((row) => row.document_id),
      ),
    listCollectionDocuments: (collectionId, options) =>
      execRows<IndexCollectionDocumentRow & SqlRow>(
        'listCollectionDocuments',
        listCollectionDocumentsSql(options?.publicOnly === true),
        collectionId,
      ),
  });
}

/** IndexStore domain layer over the runtime-provided SqlExecutor. */
export const layer: Layer.Layer<IndexStore, never, SqlExecutor> = Layer.effect(
  IndexStore,
  Effect.map(SqlExecutor, (sql) => make({ sql })),
);

/** Process-local IndexStore composed with the core memory SqlExecutor. */
export const layerMemory: Layer.Layer<IndexStore> = layer.pipe(
  Layer.provide(sqlExecutorLayerMemory),
);
